import React, { useEffect, useState } from "react";
import { saveFile } from "../../commonFunctions/fileFolderConfig";
import Button from "../../components/FormComponents/Button";
import { BASE_URL, IS_PRODUCTION, IS_SANDBOX } from "../../constant/constant";
import MaterialIcon from "../../components/BuilderComponents/MaterialIcon";
import Popover from "../../Layouts/Popover/Popover";
import ApplicationActionsPopover from "./ApplicationActionsPopover";
import { getToolTipAttributes } from "../../utils/tooltip";
import axiosInstance, { handleApiError } from "../../services/axiosService";
import { useNavigate } from "react-router-dom";
const ApplicationCard = ({
  app,
  deleteApplication,
  openAppEditModal,
  createAction,
  deployAction,
  destroyAction,
  openApp,
  setSelectedApplication,
  setIsModalOpen,
  onLock
}) => {
  const [anchorPoint, setAnchorPoint] = useState(null);
  const [appData, setAppData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    setAppData(app);
  }, [app]);

  // --- Polling Logic ---
  useEffect(() => {
    let intervalId;
    const statusesRequiringPolling = ["starting", "publishing", "stopping"];
    if (!appData) return;

    if (
      statusesRequiringPolling.includes(appData.status) ||
      statusesRequiringPolling.includes(appData.staging_status)
    ) {
      intervalId = setInterval(() => {
        if (statusesRequiringPolling.includes(appData.status)) {
          fetchStatus("prod");
        }
        if (statusesRequiringPolling.includes(appData.staging_status)) {
          fetchStatus("stage");
        }
      }, 5000);
    }

    return () => {
      clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appData?.status, appData?.staging_status]);

  const controller = new AbortController();
  useEffect(() => {
    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!appData) return null;

  const fetchStatus = async (type) => {
    try {
      const endpoint = type === "stage" ? "/application/staging-infra" : "/application/infra";
      const { data } = await axiosInstance.get(endpoint, {
        signal: controller.signal,
        timeout: 50000,
        headers: {
          "Zino-App-Id": appData.id
        }
      });

      setAppData((prevStatusData) => ({
        ...prevStatusData,
        ...(type === "stage" ? { staging_status: data?.status } : { status: data?.status }),
        ...(type === "stage" ? { staging_url: data?.web_url } : { web_url: data?.web_url }),
        pwa_url: data?.pwa_url
      }));
    } catch (error) {
      handleApiError(error);
    }
  };

  const createApplicationLink = async (type) => {
    try {
      await createAction(type);
      setAppData({
        ...appData,
        [type === "stage" ? "staging_status" : "status"]: "starting"
      });
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleDownloadRequirementPdf = async () => {
    try {
      const response = await axiosInstance.get(`/public/${appData.requirement_document_path}`, {
        responseType: "blob"
      });
      const fileName = `${appData.name}.pdf`;
      saveFile(response, fileName);
    } catch (error) {
      handleApiError(error);
    }
  };

  const destroyApplicationLink = async (type) => {
    try {
      await destroyAction(type);
      setAppData({
        ...appData,
        [type === "stage" ? "staging_status" : "status"]: "stopping"
      });
    } catch (error) {
      handleApiError(error);
    }
  };

  const openApplicationActionPopover = (e) => {
    e.stopPropagation();
    setAnchorPoint({ x: e.clientX, y: e.clientY, deflectX: -300, deflectY: 12 });
  };
  const closePopover = () => setAnchorPoint(null);

  return (
    <>
      <div className="app_item_box" key={appData.id}>
        {/* --- Header --- */}
        <div className="status_more_container">
          {appData?.logo ? (
            <div
              className="org_logo_circle"
              style={{
                backgroundImage: `url(${BASE_URL}/api/v1/public/${appData.logo})`,
                backgroundSize: "contain",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center"
              }}
            />
          ) : (
            <MaterialIcon name="deployed_code" className="default_org_img org_logo_circle" />
          )}

          <div className="d_flex gap_2">
            <MaterialIcon
              name="open_in_new"
              className="cursor_pointer"
              {...getToolTipAttributes("Open App")}
              onClick={() => openApp(appData, appData)}
            />

            <div
              onClick={() => {
                setSelectedApplication(appData.id);
                setIsModalOpen("user");
              }}
              {...getToolTipAttributes("App Users")}
            >
              <MaterialIcon name="group_add" />
            </div>

            <MaterialIcon
              name="settings"
              className="builder_icon"
              onClick={() => navigate(`./${app.id}/settings/`)}
              {...getToolTipAttributes("Settings")}
            />

            <div className="more_horiz_container">
              <MaterialIcon
                name="more_horiz"
                className="builder_icon"
                onClick={openApplicationActionPopover}
                {...getToolTipAttributes("Actions")}
              />

              {anchorPoint && (
                <Popover anchorPoint={anchorPoint} onClose={closePopover}>
                  <ApplicationActionsPopover
                    openAppEditModal={openAppEditModal}
                    app={appData}
                    setAppData={setAppData}
                    destroyApplicationLink={destroyApplicationLink}
                    createApplicationLink={createApplicationLink}
                    deleteApplication={deleteApplication}
                    deployAction={deployAction}
                    onLock={onLock}
                  />
                </Popover>
              )}
            </div>
          </div>
        </div>

        {/* --- Content --- */}
        <div className="app_content-container">
          <div className="title_url_container">
            <div className="app_item_title">{appData?.name}</div>
          </div>
        </div>

        <div className="flex_row align_center justify_between w_100">
          {appData.requirement_document_path && (
            <MaterialIcon
              name="file_save"
              className="edit_icon cursor_pointer"
              onClick={() => handleDownloadRequirementPdf()}
              title="Download requirement pdf"
            />
          )}
        </div>

        <div className="app_actions">
          <div className="locked_env_row">
            {IS_SANDBOX && (
              <div className="env_item">
                <span className="env_label">Stage:</span>
                <span className={`env_pill ${appData?.staging_status?.toLowerCase() || "stopped"}`}>
                  {appData?.staging_status || "-"}
                </span>
              </div>
            )}
            {IS_PRODUCTION && (
              <div className="env_item">
                <span className="env_label">Prod:</span>
                <span className={`env_pill ${appData?.status?.toLowerCase() || "stopped"}`}>
                  {appData?.status || "-"}
                </span>
              </div>
            )}
          </div>

          {(appData.staging_status === "stopped" || appData.staging_status === "starting") && (
            <div>
              <Button
                size="small"
                variant="secondary"
                onClick={() => createApplicationLink("stage")}
                isLoading={appData.staging_status === "starting"}
              >
                Start
              </Button>
            </div>
          )}

          {(appData.staging_status === "live" || appData.staging_status === "stopping") && (
            <div>
              <Button
                size="small"
                variant="delete"
                onClick={() => destroyApplicationLink("stage")}
                isLoading={appData.staging_status === "stopping"}
              >
                Stop
              </Button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ApplicationCard;
